// BHP-1053: makes the active customer available app-wide. It loads the active
// customer from Backend2.0 (via useActiveCustomer) and mirrors it into the
// legacy Redux customer slice so the not-yet-migrated Layout/navbar keep working
// without the old /api/v1 CustomerService + localStorage bootstrap.
import React, { useEffect, useRef } from 'react';
import { connect } from 'react-redux';
import Loading from '../../../pages/Screens/Loading';
import { onActiveCustomerChange } from '../../../actions';
import type CustomerData from '../../../Models/CustomerData';
import type { Customer } from '../domain/customer';
import { useActiveCustomer } from '../application/useActiveCustomer';

interface Props {
  children: React.ReactElement;
  onActiveCustomerChange: (customer: CustomerData) => void;
}

export function ActiveCustomerProvider({
  children,
  onActiveCustomerChange
}: Props): JSX.Element {
  const { customer, isLoading } = useActiveCustomer();

  // Latch: full-page loading is only for the initial bootstrap. Once the app has
  // rendered once, a later switch loads the new customer in-place (children stay
  // mounted) instead of blanking the whole app to the loading screen.
  const hasLoadedOnce = useRef(false);
  if (!isLoading) {
    hasLoadedOnce.current = true;
  }

  useEffect(() => {
    if (customer !== undefined) {
      onActiveCustomerChange(toLegacyCustomerData(customer));
    }
  }, [customer, onActiveCustomerChange]);

  if (isLoading && !hasLoadedOnce.current) {
    return <Loading />;
  }

  return children;
}

/**
 * Map the Backend2.0 customer onto the legacy CustomerData shape the Redux-bound
 * Layout/navbar still consume. Only display-relevant fields are meaningful;
 * the rest are defaulted until those components are migrated (later slice).
 */
function toLegacyCustomerData(customer: Customer): CustomerData {
  return {
    uuid: customer.uuid,
    type: customer.type,
    companyName: customer.companyName ?? customer.administrationFullName,
    kvkNumber: null,
    street: '',
    streetNumber: '',
    streetNumberAddition: '',
    postalCode: '',
    city: '',
    countryCode: '',
    notes: '',
    accounts: null,
    activeSubscriptions: null,
    orders: null,
    invoices: null,
    quotes: null,
    payments: null,
    active: true,
    deletedOn: null,
    createdOn: new Date(),
    updatedOn: null
  };
}

export default connect(null, { onActiveCustomerChange })(
  ActiveCustomerProvider
);
